import React, { useState, useEffect } from 'react';
import { Table, Button, message, Space, Modal } from 'antd';
import { studentService } from '../../services/studentService';
import { authService, ROLE_LEVELS } from '../../services/authService';
import { paymentService, PAYMENT_TYPES } from '../../services/paymentService';
import AV from '../../config/leancloud';

const WithdrawalVerification = () => {
  const [loading, setLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [withdrawalStudents, setWithdrawalStudents] = useState([]);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        const canAccess = await authService.canAccess(ROLE_LEVELS.FINANCE);
        setHasPermission(canAccess);
      } catch (error) {
        console.error('Permission check error:', error);
        message.error('权限检查失败');
      }
    };
    checkPermission();
  }, []);

  const fetchWithdrawalStudents = async () => {
    try {
      setLoading(true);
      console.log('Fetching withdrawal students...');
      const students = await studentService.getWithdrawalStudents();
      console.log('Fetched withdrawal students:', students);
      setWithdrawalStudents(students);
    } catch (error) {
      console.error('Error fetching withdrawal students:', error);
      message.error('获取退证申请失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawalStudents();
  }, []);

  const handleVerification = async (approved) => {
    if (selectedRows.length === 0) {
      message.warning('请选择要审核的退证申请');
      return;
    }

    Modal.confirm({
      title: `确认${approved ? '通过' : '不通过'}所选退证申请？`,
      content: `将${approved ? '通过' : '不通过'} ${selectedRows.length} 个退证申请`,
      onOk: async () => {
        try {
          setLoading(true);
          
          // Process each withdrawal verification
          await Promise.all(
            selectedRows.map(async student => {
              // First verify the withdrawal
              await studentService.verifyWithdrawal(student.objectId, approved);
              
              // If approved, create refund payment record
              if (approved) {
                try {
                  // Convert withdraw receipt URL to File if it exists
                  let receiptFile = null;
                  if (student.withdrawReceipt) {
                    const response = await fetch(student.withdrawReceipt);
                    const blob = await response.blob();
                    receiptFile = new File([blob], 'withdraw-receipt.jpg', { type: 'image/jpeg' });
                  }

                  await paymentService.createPayment({
                    student: new AV.Object.createWithoutData('Student', student.objectId),
                    amount: student.tuitionFee || 0,
                    type: PAYMENT_TYPES.REFUND,
                    description: `退证原因: ${student.withdrawReason || '未说明'}`,
                    receipt: receiptFile,
                    date: new Date()
                  });
                } catch (paymentError) {
                  console.error('Failed to create refund payment record:', paymentError);
                  message.warning(`学员 ${student.name} 的退款记录创建失败，但退证状态已更新`);
                }
              }
            })
          );
          
          message.success('审核操作成功');
          fetchWithdrawalStudents();
          setSelectedRows([]);
        } catch (error) {
          console.error('Verification error:', error);
          message.error('审核操作失败');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '身份证号',
      dataIndex: 'uniqueId',
      key: 'uniqueId',
    },
    {
      title: '学费',
      dataIndex: 'tuitionFee',
      key: 'tuitionFee',
    },
    {
      title: '退证原因',
      dataIndex: 'withdrawReason',
      key: 'withdrawReason',
    },
    {
      title: '退证收据',
      key: 'withdrawReceipt',
      render: (_, record) => (
        record.withdrawReceipt ? 
          <a href={record.withdrawReceipt} target="_blank" rel="noopener noreferrer">
            查看收据
          </a> : 
          '无'
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys: selectedRows.map(row => row.objectId),
    onChange: (_, rows) => setSelectedRows(rows),
    columnWidth: 60,
    fixed: true
  };

  const onRow = (record) => ({
    onClick: () => {
      const isSelected = selectedRows.some(row => row.objectId === record.objectId);
      if (isSelected) {
        setSelectedRows(selectedRows.filter(row => row.objectId !== record.objectId));
      } else {
        setSelectedRows([...selectedRows, record]);
      }
    },
    style: {
      cursor: 'pointer',
      background: selectedRows.some(row => row.objectId === record.objectId) 
        ? '#e6f7ff' 
        : undefined
    }
  });

  if (!hasPermission) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px', 
        fontSize: '16px', 
        color: '#ff4d4f' 
      }}>
        您没有权限访问此页面，请联系管理员
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button 
          type="primary" 
          onClick={() => handleVerification(true)}
          disabled={selectedRows.length === 0}
        >
          通过退证
        </Button>
        <Button 
          danger 
          onClick={() => handleVerification(false)}
          disabled={selectedRows.length === 0}
        >
          拒绝退证
        </Button>
      </Space>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={withdrawalStudents}
        loading={loading}
        rowKey="objectId"
        onRow={onRow}
      />
    </div>
  );
};

export default WithdrawalVerification; 